import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, PackageX } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useStats } from "@/context/StatsContext";

export default function StockBell() {
  const { t } = useLang();
  const { user } = useAuth();
  const { stats } = useStats();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const rows = stats?.low_stock || [];
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  React.useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  if (!rows.length) return null;

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((o) => !o)} data-testid="stock-alert-bell" title={t("low_stock_alert")}
        className="relative grid h-8 w-8 sm:h-9 sm:w-9 place-items-center rounded-full border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 transition-transform hover:scale-105 active:scale-95">
        <PackageX className="h-4 w-4" />
        <span data-testid="stock-alert-count"
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-rose-600 text-white text-[10px] font-bold tabular-nums">
          {rows.length}
        </span>
      </button>

      {open && (
        <div data-testid="stock-alert-panel"
          className="fixed left-3 right-3 top-[4.25rem] w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 max-h-96 overflow-y-auto cb-scroll rounded-2xl border border-border bg-card shadow-xl z-50 p-3 animate-in fade-in-0 zoom-in-95">
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-2">
            <AlertTriangle className="h-3.5 w-3.5" />{t("low_stock_alert")}
          </p>
          <ul className="space-y-1.5">
            {rows.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2 text-sm border-b border-border/60 pb-1.5 last:border-0">
                <span className="min-w-0">
                  <span className="block truncate font-medium text-foreground">{a.name}</span>
                  <span className="text-[11px] text-muted-foreground">{a.code}</span>
                </span>
                <span className="shrink-0 text-right tabular-nums">
                  <span className="font-display font-extrabold text-rose-600">{a.stock}</span>
                  <span className="text-[11px] text-muted-foreground"> / {a.min_stock} {a.unit}</span>
                </span>
              </li>
            ))}
          </ul>
          {isAdmin && (
            <button onClick={() => { setOpen(false); navigate("/admin/voorraad"); }} data-testid="stock-alert-goto"
              className="mt-3 w-full rounded-full bg-primary text-primary-foreground text-xs font-semibold py-2 hover:opacity-90">
              {t("goto_stock")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
